import React, { useState } from 'react';
import { ShareService } from '../services/shareService';
import ShareOptionsModal from './ShareOptionsModal';
import UserSearchModal from './UserSearchModal';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyRole } from '../hooks/useFamilyRole';

/* EXPANDABLE EVENT CARD COMPONENT - Collapsible cards for calendar view */
/* Used on CalendarPage to show events in daily lists */
/* White cards with colored accents that expand to show details */
const ExpandableEventCard = ({
  event,
  onEdit,
  onDelete,
  onAccept,
  onIgnore,
  getCategoryStyles,
  formatDateTime,
  isMobile,
  isPending = false,
  displayDate = null
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showUserSearchModal, setShowUserSearchModal] = useState(false);
  const { currentUser } = useAuth();
  const { canEditEvents, canDeleteEvents } = useFamilyRole();
  
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  /* CATEGORY COLORS - Returns color codes for event type */
  /* work=blue, school=purple, sport=red, life=green */
  const getCardBackground = () => {
    if (!event.category) return "#4361ee"; // Default blue
    
    switch(event.category.toLowerCase()) {
      case 'work':
        return "#1c83c9"; // Blue
      case 'school':
        return "#5550B7"; // Purple
      case 'sport':
        return "#E63946"; // Red
      case 'life':
        return "#2A9D8F"; // Green
      default:
        return "#4361ee"; // Default blue
    }
  };

  /* DELETE HANDLER - Special logic for recurring events */
  /* Shows dialog to delete single instance or entire series */
  const handleDelete = (e) => {
    if (e) e.stopPropagation();

    // If this is a recurring event or a recurring instance
    if (event.isRecurring || event.isRecurrenceInstance) {
      // Show a custom dialog for recurring events
      const deleteOption = window.confirm(
        `"${event.eventName}" is a recurring event. What would you like to delete?\n\n` +
        `• Click OK to delete the entire recurring series\n` +
        `• Click Cancel to delete only this occurrence`
      );

      if (deleteOption) {
        // Delete the entire series - use the original event ID
        const originalId = event.originalEventId || event.id;
        onDelete(originalId);
      } else {
        // Delete just this specific instance
        // For now, just delete the original event since we don't have a mechanism
        // to track deleted instances yet. This would need to be expanded in the future.
        if (event.isRecurrenceInstance) {
          // Create an exception record or handle the specific instance deletion
          // This would require tracking exceptions in the database
          alert("Deleting a single instance of a recurring event is not fully implemented yet.");
        } else {
          onDelete(event.id);
        }
      }
    } else {
      // Regular non-recurring event - simple confirmation
      if (window.confirm(`Are you sure you want to delete "${event.eventName}"?`)) {
        onDelete(event.id);
      }
    }
  };

  // Handle edit event
  const handleEdit = (e) => {
    if (e) e.stopPropagation();
    onEdit(event);
  };

  /* SHARE HANDLER - Opens share options modal */
  const handleShare = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setShowShareModal(true);
  };

  /* SHARE OPTION HANDLER - Handles different share options */
  const handleShareOption = async (option) => {
    switch (option) {
      case 'whatsapp':
        ShareService.shareViaWhatsApp(event, formatDateTime, displayDate);
        break;
      case 'signup':
        ShareService.shareViaWhatsAppWithSignup(event, formatDateTime, displayDate);
        break;
      case 'user':
        setShowUserSearchModal(true);
        break;
      default:
        console.warn('Unknown share option:', option);
    }
  };

  /* USER SHARE HANDLER - Shares event with specific user */
  const handleShareWithUser = async (recipientEmail) => {
    if (!currentUser) {
      alert('You must be logged in to share events with other users');
      return;
    }

    try {
      const result = await ShareService.shareWithUser(event, recipientEmail, currentUser.uid);
      if (result.success) {
        alert(result.message);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error sharing event:', error);
      alert('Failed to share event. Please try again.');
    }
  };


  /* CARD STYLING - White background with minimal shadow */
  /* Duplicate events get yellow background with border */
  const cardStyle = {
    backgroundColor: '#ffffff', // Always white background
    color: '#555555', // Grey text
    borderRadius: '12px',
    padding: isMobile ? '10px 10px' : '15px',
    paddingBottom: isMobile ? (isExpanded ? '10px' : '20px') : (isExpanded ? '20px' : '30px'), // Reduced padding
    boxShadow: 'none', // Removed shadow
    position: 'relative',
    minHeight: isMobile ? '80px' : '100px', // Reduced minimum height
    width: isMobile ? 'calc(100% - 20px)' : '100%',
    boxSizing: 'border-box',
    marginBottom: isMobile ? '4px' : '5px', // Reduced margin
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    margin: isMobile ? '0 auto 4px auto' : '0 auto 5px auto', // Reduced margin
    left: '0',
    right: '0'
  };

  const eventIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '8px'
  };

  const eventTitleStyle = {
    fontWeight: '600',
    fontSize: isMobile ? '0.95rem' : '1.1rem',
    marginBottom: isMobile ? '2px' : '4px',
    color: getCardBackground(), // Use category color for title
    flex: 1 // Allow the title to take up available space
  };

  const eventTimeStyle = {
    fontSize: isMobile ? '0.9rem' : '1rem',
    color: '#555555',
    opacity: 0.85,
    fontWeight: '500'
  };

  const deleteButtonStyle = {
    backgroundColor: 'transparent',
    color: getCardBackground(), // Match the title color
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '0.9rem',
    marginTop: '20px',
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    cursor: 'pointer'
  };

  const expandedContentStyle = {
    marginTop: isMobile ? '8px' : '10px',
    display: isExpanded ? 'block' : 'none',
    width: '100%'
  };

  /* EXPANDABLE CARD UI - Click anywhere to expand/collapse */
  /* Shows basic info collapsed, full details when expanded */
  return (
    <>
      <div 
        className="expandable-event-card"
        data-event-id={event.id}
        style={cardStyle}
        onClick={toggleExpand}
      >
      {/* COLLAPSED VIEW - Always visible section with title and time */}
      {/* Shows category icon, name, time, and participant chips */}
      <div>
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px', width: '100%'}}>
          <div style={eventIconStyle}>
            {/* Different icons based on event category */}
            {event.category === 'work' && (
              <img
                src="/briefcase.svg"
                alt="Work"
                width={isMobile ? "20" : "24"}
                height={isMobile ? "20" : "24"}
                style={{filter: `brightness(0) saturate(100%) invert(47%) sepia(83%) saturate(1347%) hue-rotate(175deg) brightness(94%) contrast(85%)`}}
              />
            )}
            {event.category === 'school' && (
              <img
                src="/graduation-cap.svg"
                alt="School"
                width={isMobile ? "20" : "24"}
                height={isMobile ? "20" : "24"}
                style={{filter: `brightness(0) saturate(100%) invert(33%) sepia(57%) saturate(1720%) hue-rotate(238deg) brightness(92%) contrast(88%)`}}
              />
            )}
            {event.category === 'class' && (
              <img
                src="/group.png"
                alt="Class"
                width={isMobile ? "20" : "24"}
                height={isMobile ? "20" : "24"}
                style={{filter: `brightness(0) saturate(100%) invert(33%) sepia(57%) saturate(1720%) hue-rotate(238deg) brightness(92%) contrast(88%)`}}
              />
            )}
            {event.category === 'sport' && (
              <img
                src="/tennis.svg"
                alt="Sport"
                width={isMobile ? "20" : "24"}
                height={isMobile ? "20" : "24"}
                style={{filter: `brightness(0) saturate(100%) invert(36%) sepia(51%) saturate(2335%) hue-rotate(338deg) brightness(87%) contrast(87%)`}}
              />
            )}
            {event.category === 'life' && (
              <img
                src="/circle-star.svg"
                alt="Life"
                width={isMobile ? "20" : "24"}
                height={isMobile ? "20" : "24"}
                style={{filter: `brightness(0) saturate(100%) invert(46%) sepia(72%) saturate(512%) hue-rotate(88deg) brightness(89%) contrast(91%)`}}
              />
            )}
            {(!event.category || event.category === 'other') && (
              <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "20" : "24"} height={isMobile ? "20" : "24"} viewBox="0 0 24 24" fill="none" stroke={getCardBackground()} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            )}
          </div>
          <div style={{...eventTitleStyle, marginLeft: '8px', marginBottom: 0}}>
            {event.eventName}
            {(event.isRecurring || event.isRecurrenceInstance) && (
              <span style={{
                marginLeft: '6px',
                fontSize: isMobile ? '10px' : '12px',
                color: getCardBackground()
              }} title="Recurring event">↻</span>
            )}
          </div>
          <button
            className="action-button share-button"
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 5,
              minWidth: '32px',
              minHeight: '32px'
            }}
            onClick={handleShare}
            title="Share Event"
          >
            {/* Share icon */}
            <img
              src="/share.png"
              alt="Share"
              width={isMobile ? "20" : "22"}
              height={isMobile ? "20" : "22"}
              style={{filter: `brightness(0) saturate(100%) ${getCardBackground() === "#4361ee" ?
                "invert(29%) sepia(93%) saturate(1963%) hue-rotate(222deg) brightness(97%) contrast(91%)" :
                `invert(${getCardBackground() === "#1c83c9" ? "47" : getCardBackground() === "#5550B7" ? "33" :
                  getCardBackground() === "#E63946" ? "36" : "46"}%)
                  sepia(${getCardBackground() === "#1c83c9" ? "83" : getCardBackground() === "#5550B7" ? "57" :
                    getCardBackground() === "#E63946" ? "51" : "72"}%)
                  saturate(${getCardBackground() === "#1c83c9" ? "1347" : getCardBackground() === "#5550B7" ? "1720" :
                    getCardBackground() === "#E63946" ? "2335" : "512"}%)
                  hue-rotate(${getCardBackground() === "#1c83c9" ? "175" : getCardBackground() === "#5550B7" ? "238" :
                    getCardBackground() === "#E63946" ? "338" : "88"}deg)
                  brightness(${getCardBackground() === "#1c83c9" ? "94" : getCardBackground() === "#5550B7" ? "92" :
                    getCardBackground() === "#E63946" ? "87" : "89"}%)
                  contrast(${getCardBackground() === "#1c83c9" ? "85" : getCardBackground() === "#5550B7" ? "88" :
                    getCardBackground() === "#E63946" ? "87" : "91"}%)`}`
              }}
            />
          </button>
        </div>
        <div style={{...eventTimeStyle, marginTop: isMobile ? '4px' : '6px', width: '100%'}}>
          {formatDateTime(event)}
        </div>
        {/* Show event source and creator */}
        {(event.createdByName || event.eventSource || event.source) && (
          <div style={{
            marginTop: isMobile ? '12px' : '16px',
            fontSize: isMobile ? '0.75rem' : '0.85rem',
            color: '#888',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            {(event.eventSource || event.source) && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
                {(() => {
                  const source = event.eventSource || event.source || 'manual';
                  switch(source) {
                    case 'manual':
                      return 'Added by family member';
                    case 'email':
                      return 'Extracted from school email';
                    case 'email_attachment':
                      return 'Extracted from email attachment';
                    case 'gcal':
                    case 'google_calendar':
                      return 'Synced from Google Calendar';
                    case 'paste':
                      return 'Generated from pasted text';
                    case 'document':
                    case 'pdf':
                      return 'Scanned from uploaded file';
                    case 'whatsapp':
                      return 'Received via WhatsApp';
                    default:
                      return 'Added by family member';
                  }
                })()}
              </span>
            )}
            {event.createdByName && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                {event.createdByName}
              </span>
            )}
          </div>
        )}
        {/* Show participants as oval chips in the collapsed view */}
        {((event.participants && event.participants.length > 0) || event.participant) && (
          <div style={{
            marginTop: isMobile ? '8px' : '10px',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px'
          }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px'
            }}>
              {event.participants && Array.isArray(event.participants)
                ? event.participants.map((personEmail, index) => {
                    // Check if we have participantNames field with actual names
                    const displayName = event.participantNames && event.participantNames[index] 
                      ? event.participantNames[index] 
                      : personEmail.includes('@') 
                        ? personEmail.split('@')[0] // If it's an email, show username part
                        : personEmail; // Otherwise show as is
                    
                    return (
                      <div key={index} style={{
                        backgroundColor: `${getCardBackground()}15`, // Very light version of the category color
                        color: getCardBackground(),
                        borderRadius: '14px',
                        padding: '3px 8px',
                        fontSize: isMobile ? '0.75rem' : '0.85rem',
                        display: 'inline-block',
                        border: `1px solid ${getCardBackground()}30`
                      }}>
                        {displayName}
                      </div>
                    );
                  })
                : event.participant.split(',').map((personEmail, index) => {
                    const trimmedPerson = personEmail.trim();
                    // Check if we have participantNames field with actual names
                    const displayName = event.participantNames && event.participantNames[index] 
                      ? event.participantNames[index] 
                      : trimmedPerson.includes('@') 
                        ? trimmedPerson.split('@')[0] // If it's an email, show username part
                        : trimmedPerson; // Otherwise show as is
                    
                    return (
                      <div key={index} style={{
                        backgroundColor: `${getCardBackground()}15`, // Very light version of the category color
                        color: getCardBackground(),
                        borderRadius: '14px',
                        padding: '3px 8px',
                        fontSize: isMobile ? '0.75rem' : '0.85rem',
                        display: 'inline-block',
                        border: `1px solid ${getCardBackground()}30`
                      }}>
                        {displayName}
                      </div>
                    );
                  })
              }
            </div>
          </div>
        )}
      </div>
      
      {/* EXPANDED CONTENT - Additional details shown on click */}
      {/* Displays description, location, and recurrence info */}
      <div style={expandedContentStyle}>
        {/* Show description in the expanded view */}
        {event.description && (
          <div style={{
            marginBottom: isMobile ? '10px' : '12px',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            color: '#555555',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '5px'
            }}>
              <div 
                dangerouslySetInnerHTML={{
                  __html: event.description.replace(
                    /(https?:\/\/[^\s]+)/g, 
                    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #007bff; text-decoration: underline;" onclick="event.stopPropagation();">$1</a>'
                  )
                }}
                onClick={(e) => {
                  // Allow clicks on links to work by stopping propagation
                  if (e.target.tagName === 'A') {
                    e.stopPropagation();
                  }
                }}
              />
            </div>
          </div>
        )}

        {event.location && (
          <div style={{
            marginBottom: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            color: '#555555',
            display: 'flex',
            alignItems: 'center',
            width: '100%'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "12" : "14"} height={isMobile ? "12" : "14"} viewBox="0 0 24 24" fill="none" stroke={getCardBackground()} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: isMobile ? '6px' : '8px'}}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            {event.location}
          </div>
        )}

        {/* Show recurrence pattern if this is a recurring event */}
        {(event.isRecurring || event.isRecurrenceInstance) && (
          <div style={{
            marginBottom: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            color: '#555555',
            display: 'flex',
            alignItems: 'center',
            width: '100%'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "12" : "14"} height={isMobile ? "12" : "14"} viewBox="0 0 24 24" fill="none" stroke={getCardBackground()} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: isMobile ? '6px' : '8px'}}>
              <path d="M3 12h18"></path>
              <path d="M16 6l6 6-6 6"></path>
              <path d="M8 18l-6-6 6-6"></path>
            </svg>
            {event.recurrencePattern === 'daily' && `Repeats daily`}
            {event.recurrencePattern === 'weekdays' && `Repeats on weekdays`}
            {event.recurrencePattern === 'weekly' && `Repeats weekly`}
            {event.recurrencePattern === 'monthly' && `Repeats monthly`}
            {event.recurrencePattern === 'yearly' && `Repeats yearly`}
            {event.recurrencePattern === 'custom' && `Repeats on selected days`}
            {event.recurrenceInterval > 1 && ` every ${event.recurrenceInterval} ${event.recurrencePattern.replace('ly', '')}`}
            {event.recurrenceCount > 0 && ` (${event.recurrenceCount} times)`}
            {event.recurrenceEndDate && ` until ${new Date(event.recurrenceEndDate).toLocaleDateString()}`}
          </div>
        )}

        {/* Participants are already shown in collapsed view, could add fancy formatted chips here if needed */}
        {/* But we hide them for now to avoid duplication */}
      </div>
      
      {/* COLLAPSED ACTION BUTTONS - Shows different buttons based on state */}
      {/* Pending events: Accept/Ignore, Accepted events: Edit button */}
      {!isExpanded && (
        <div style={{
          position: 'absolute',
          bottom: '8px', // Bottom for all devices
          right: isMobile ? '8px' : '12px',
          width: isPending ? (isMobile ? '100%' : '70%') : 'auto',
          display: 'flex',
          justifyContent: 'flex-end',
          zIndex: 10
        }}>
          {isPending ? (
            <>
              <button
                style={{
                  backgroundColor: 'rgba(40, 167, 69, 0.1)',
                  color: '#28a745', // Green color for accept
                  border: '1px solid #28a745',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '0.9rem',
                  marginRight: '8px',
                  cursor: 'pointer',
                  display: 'inline-block',
                  minWidth: '70px',
                  fontWeight: '600'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onAccept) onAccept(event.id);
                }}
              >
                Accept
              </button>
              <button
                style={{
                  backgroundColor: 'rgba(220, 53, 69, 0.1)',
                  color: '#dc3545', // Red color for ignore
                  border: '1px solid #dc3545',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'inline-block',
                  minWidth: '70px',
                  fontWeight: '600'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onIgnore) onIgnore(event.id);
                }}
              >
                Ignore
              </button>
            </>
          ) : canEditEvents ? (
            <button
              style={{
                backgroundColor: 'transparent',
                color: '#666666', // Grey color
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '0.9rem',
                textAlign: 'right',
                display: 'flex',
                justifyContent: 'flex-end',
                cursor: 'pointer'
              }}
              onClick={handleEdit}
            >
              Edit
            </button>
          ) : null}
        </div>
      )}
      
      {/* EXPANDED DELETE BUTTON - Trash icon in bottom right */}
      {/* Only visible when card is expanded */}
      {isExpanded && canDeleteEvents && (
        <div style={{
          marginTop: '0',
          alignSelf: 'flex-end',
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          padding: '0'
        }}>
          <button
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={handleDelete}
            title="Delete Event"
          >
            <img
              src="/trash-can.png"
              alt="Delete"
              width={isMobile ? "18" : "20"}
              height={isMobile ? "18" : "20"}
              style={{
                filter: 'brightness(0) saturate(100%) invert(36%) sepia(51%) saturate(2335%) hue-rotate(338deg) brightness(87%) contrast(87%)',
                opacity: 0.8,
                transition: 'opacity 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = 1}
              onMouseOut={(e) => e.currentTarget.style.opacity = 0.8}
            />
          </button>
        </div>
      )}
      </div>
      
      {/* Share Options Modal - Rendered outside card for proper positioning */}
      {showShareModal && (
        <ShareOptionsModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          onSelectOption={handleShareOption}
          event={event}
          formatDateTime={formatDateTime}
          displayDate={displayDate}
        />
      )}
      
      {/* User Search Modal - Rendered outside card for proper positioning */}
      {showUserSearchModal && (
        <UserSearchModal
          isOpen={showUserSearchModal}
          onClose={() => setShowUserSearchModal(false)}
          onSelectUser={handleShareWithUser}
        />
      )}
    </>
  );
};

export default ExpandableEventCard;